import Button from 'tio-ui/components/button';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import Form from 'tio-ui/components/form';
import type { FormResult } from 'tio-ui/components/form';
import Textarea from 'tio-ui/components/textarea';
import { on } from '@ember/modifier';
import { PhX, PhTrash, PhPencil, PhCheck } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import type CommentModel from 'tio-data/models/comment';
import type Owner from '@ember/owner';
import * as v from 'valibot';

const commentSchema = v.object({
  comment: v.pipe(v.string(), v.nonEmpty('Comment cannot be empty')),
});

type CommentSchema = v.InferOutput<typeof commentSchema>;

interface ApproverCommentsTableRowSignature {
  Args: {
    canDelete: boolean;
    canEdit: boolean;
    model: CommentModel;
    onCancel: (model: CommentModel) => Promise<void>;
    onDelete: (model: CommentModel) => Promise<void>;
    onSave: (model: CommentModel) => Promise<void>;
  };
}

export default class ApproverCommentsTableRowComponent extends Component<ApproverCommentsTableRowSignature> {
  @tracked isEditing = false;
  @tracked isDirty = false;
  @tracked originalComment = '';
  @tracked error: Error | null = null;
  @tracked showDeleteConfirm = false;

  constructor(owner: Owner, args: ApproverCommentsTableRowSignature['Args']) {
    super(owner, args);
    if (this.args.model?.isNew) {
      this.isEditing = true;
      this.originalComment = this.args.model.comment;
    }
  }

  get formData() {
    return { comment: this.args.model.comment || '' };
  }

  validate = (data: CommentSchema) => {
    const r = v.safeParse(commentSchema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  startEdit = () => {
    this.isDirty = false;
    this.isEditing = true;
    this.originalComment = this.args.model.comment;
  };

  cancel = () => {
    this.isEditing = false;
    this.isDirty = false;
    this.error = null;
    this.args.onCancel(this.args.model);
  };

  formDidChange = (formResult: FormResult<CommentSchema>) => {
    this.args.model.comment = formResult.data.comment;
    this.isDirty = formResult.data.comment !== this.originalComment;
  };

  handleSubmit = async (formResult: FormResult<CommentSchema>) => {
    // If form hasn't been modified, just cancel editing
    if (!this.isDirty) {
      this.isEditing = false;
      return;
    }

    // Update model with validated data
    this.args.model.comment = formResult.data.comment;

    try {
      await this.args.onSave(this.args.model);
      this.isEditing = false;
      this.error = null;
    } catch (e) {
      this.error = e;
    }
  };

  confirmDelete = () => {
    this.showDeleteConfirm = true;
  };

  delete = dropTask(async () => {
    await this.args.onDelete(this.args.model);
    this.showDeleteConfirm = false;
  });

  <template>
    <tr class="align-middle">
      <td class="p-4">
        <div class="tio-row-between">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-semibold">{{@model.commentor}}</span>
            <time datetime={{@model.createdAt}} class="tio-fg-muted text-sm">
              {{safeFormatDate @model.createdAt weekday="long" month="long" day="2-digit"}}
              |
              {{safeFormatDate @model.createdAt hour="numeric" minute="numeric"}}
            </time>
          </div>

          <div class="flex gap-x-2">
            {{#if this.isEditing}}{{else}}
              {{#if @canEdit}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.startEdit}}
                  data-test-edit-button
                  title="Edit"
                >
                  <PhPencil @size="16" />
                </Button>
              {{/if}}
              {{#if @canDelete}}
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.confirmDelete}}
                  data-test-delete-button
                  title="Delete"
                >
                  <PhTrash @size="16" />
                </Button>
              {{/if}}
            {{/if}}
          </div>
        </div>

        <div class="mt-2">
          {{#if this.isEditing}}
            <Form
              @data={{this.formData}}
              @validate={{this.validate}}
              @onChange={{this.formDidChange}}
              @onSubmit={{this.handleSubmit}}
              class="tio-col-loose"
              as |form|
            >
              {{#let (form.field "comment") as |f|}}
                <Textarea
                  @value={{f.value}}
                  @onChange={{f.onChange}}
                  @onBlur={{f.onBlur}}
                  @errors={{f.errors}}
                  @label="Comment"
                  @rows={{3}}
                  class="w-full border tio-border-default p-1"
                  aria-label="Comment"
                />
              {{/let}}

              {{#if this.error}}
                <p class="text-red-600">{{this.error.message}}</p>
              {{/if}}

              <div class="flex gap-x-2">
                <Button type="submit" @intent="primary" title="Save" data-test-save-button>
                  <PhCheck @size="16" />
                </Button>
                <Button
                  type="button"
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.cancel}}
                  title="Cancel"
                  data-test-cancel-button
                >
                  <PhX @size="16" />
                </Button>
              </div>
            </Form>
          {{else}}
            <p class="whitespace-normal break-words">{{@model.comment}}</p>
            {{#unless (eq @model.createdAt @model.updatedAt)}}
              <div class="tio-text-caption mt-1">
                {{t "common.tuition_assistance.last_updated"}}
                {{safeFormatDate
                  @model.updatedAt
                  month="long"
                  day="2-digit"
                  hour="numeric"
                  minute="numeric"
                }}
              </div>
            {{/unless}}
          {{/if}}
        </div>
      </td>
    </tr>

    <ConfirmationDialog
      @isOpen={{this.showDeleteConfirm}}
      @title="Delete Comment"
      @intent="danger"
      @onConfirm={{this.delete.perform}}
      @onCancel={{fn (mut this.showDeleteConfirm) false}}
      @isRunning={{this.delete.isRunning}}
      @confirmText="Delete"
    >
      <p>{{t "common.tuition_assistance.are_you_sure_you_want_to_delete"}}</p>
      <p>{{t "common.tuition_assistance.this_action_cannot_be_undone"}}</p>
    </ConfirmationDialog>
  </template>
}
